import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Leaf, Droplets, Zap, TrendingUp, BookOpen,
  CalendarCheck, Scale, Star, Edit2, Sprout, Image as ImageIcon,
  ChevronDown, ChevronUp, FlaskConical, CalendarDays, X, Save,
} from 'lucide-react';
import DatePicker from '../components/DatePicker';

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_IMG = '/plant-placeholder.svg';
const resolveImageUrl = (url) => {
  if (!url) return FALLBACK_IMG;
  if (url.startsWith('http') || url.startsWith('/plant-placeholder')) return url;
  return `${API}${url}`;
};

const PHASE_OPTIONS = [
  { value: 'germination', label: 'Keimung' },
  { value: 'seedling',    label: 'Sämling' },
  { value: 'vegetative',  label: 'Vegetativ' },
  { value: 'flowering',   label: 'Blüte' },
  { value: 'harvest',     label: 'Ernte' },
];
const PHASE_LABELS = Object.fromEntries(PHASE_OPTIONS.map(p => [p.value, p.label]));

const CARE_COLORS = {
  watering:    { bg: 'rgba(59,130,246,0.15)',  text: '#60A5FA' },
  fertilizing: { bg: 'rgba(245,158,11,0.15)',  text: '#FBBF24' },
  training:    { bg: 'rgba(16,185,129,0.15)',  text: '#34D399' },
  topping:     { bg: 'rgba(139,92,246,0.15)',  text: '#A78BFA' },
  defoliation: { bg: 'rgba(239,68,68,0.15)',   text: '#F87171' },
  repotting:   { bg: 'rgba(217,119,6,0.15)',   text: '#FCD34D' },
  other:       { bg: 'rgba(107,114,128,0.15)', text: '#9CA3AF' },
};
const CARE_LABELS = {
  watering: 'Gießen', fertilizing: 'Düngen', training: 'Trainieren',
  topping: 'Topping', defoliation: 'Entblätterung', repotting: 'Umtopfen', other: 'Sonstiges',
};
const CARE_ICONS = {
  watering: Droplets, fertilizing: Zap, training: TrendingUp,
  topping: Leaf, defoliation: Leaf, repotting: Sprout, other: CalendarCheck,
};

function careColor(type) { return CARE_COLORS[type] || CARE_COLORS.other; }

export default function PlantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [plant,    setPlant]    = useState(null);
  const [diary,    setDiary]    = useState([]);
  const [strains,  setStrains]  = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [loading,  setLoading]  = useState(true);

  const [showAllCare,    setShowAllCare]    = useState(false);
  const [expandedDiary,  setExpandedDiary]  = useState(null);

  // Edit state
  const [showEdit,   setShowEdit]   = useState(false);
  const [editForm,   setEditForm]   = useState({});
  const [saving,     setSaving]     = useState(false);
  const [editError,  setEditError]  = useState('');

  const load = useCallback(async () => {
    try {
      const [pRes, dRes, sRes, gRes] = await Promise.all([
        axios.get(`${API}/api/plants/${id}`),
        axios.get(`${API}/api/diary?plant_id=${id}`),
        axios.get(`${API}/api/strains`),
        axios.get(`${API}/api/groups`),
      ]);
      setPlant(pRes.data);
      setDiary(Array.isArray(dRes.data) ? dRes.data : []);
      setStrains(Array.isArray(sRes.data) ? sRes.data : []);
      setGroups(Array.isArray(gRes.data) ? gRes.data : []);
    } catch {}
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setEditForm({
      name:          plant.name || '',
      phase:         plant.phase || 'seedling',
      strain_id:     plant.strain_id || '',
      grow_group_id: plant.grow_group_id || '',
      start_date:    plant.start_date || '',
      notes:         plant.notes || '',
      
    });
    setEditError('');
    setShowEdit(true);
  };



  const handleSave = async (e) => {
    e.preventDefault();
    if (!editForm.name?.trim()) { setEditError('Name ist erforderlich'); return; }
    setSaving(true);
    setEditError('');
    try {
      const payload = {
        name:          editForm.name,
        phase:         editForm.phase,
        strain_id:     editForm.strain_id || null,
        grow_group_id: editForm.grow_group_id || null,
        start_date:    editForm.start_date || null,
        notes:         editForm.notes || '',

      };
      const { data } = await axios.put(`${API}/api/plants/${id}`, payload);
      setPlant(data);
      setShowEdit(false);
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!plant) return (
    <div className="p-6 text-center">
      <p className="text-emerald-100/50">Pflanze nicht gefunden</p>
      <Link to="/plants" className="text-emerald-400 mt-2 inline-block">Zurück</Link>
    </div>
  );

  const careToShow = showAllCare ? (plant.care_history || []) : (plant.care_history?.slice(0, 5) || []);
  const harvest    = plant.harvest;
  const strain     = plant.strain;

  return (
    <div className="animate-fade-in" data-testid="plant-detail-page">

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="relative h-52 md:h-72 overflow-hidden">
        <img
          src={resolveImageUrl(plant.display_image)}
          alt={plant.name}
          onError={(e) => { e.target.src = '/plant-placeholder.svg'; }}
          className="w-full h-full"
          style={plant.display_image ? {objectFit:'cover'} : {objectFit:'contain', padding:'32px', background:'linear-gradient(135deg, #0A120D 0%, #0F1F14 50%, #0A1A0E 100%)'}}
        />
        <div className="absolute inset-0"
          style={{ background: plant.display_image
            ? 'linear-gradient(to top, rgba(5,10,7,0.85) 0%, rgba(5,10,7,0.3) 50%, transparent 100%)'
            : 'linear-gradient(to top, rgba(5,10,7,0.85) 0%, rgba(5,10,7,0.1) 100%)',
          }} />

        {/* Back */}
        <button
          onClick={() => navigate('/plants')}
          className="absolute top-4 left-4 w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
          style={{ background: 'rgba(5,10,7,0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="plant-detail-back-btn"
        >
          <ArrowLeft size={18} className="text-emerald-100" />
        </button>

        {/* Top-right: phase + edit */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <span className={`badge badge-green phase-${plant.phase} text-sm px-3 py-1`}
            style={{ backdropFilter: 'blur(8px)' }}>
            {PHASE_LABELS[plant.phase] || plant.phase}
          </span>
          <button
            onClick={openEdit}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
            style={{ background: 'rgba(5,10,7,0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid="plant-edit-btn"
          >
            <Edit2 size={15} className="text-emerald-100" />
          </button>
        </div>

        {/* Name + meta */}
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
          <h1 className="font-heading text-3xl font-bold text-white leading-tight">{plant.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1.5">
            {plant.project_name && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-100/70">
                <Sprout size={13} className="text-emerald-400" /> {plant.project_name}
              </span>
            )}
            {strain && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-100/70">
                <FlaskConical size={13} className="text-emerald-400" /> {strain.name}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="p-4 md:p-6 space-y-5">

        {/* Info kacheln */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {plant.start_date && (
            <div className="card-grow p-3 text-center">
              <CalendarDays size={16} className="mx-auto text-emerald-400 mb-1" />
              <p className="text-xs text-emerald-100/50">Startdatum</p>
              <p className="text-sm font-semibold text-emerald-100 mt-0.5">
                {new Date(plant.start_date).toLocaleDateString('de-DE')}
              </p>
            </div>
          )}
          {plant.care_history?.length > 0 && (
            <div className="card-grow p-3 text-center">
              <CalendarCheck size={16} className="mx-auto text-emerald-400 mb-1" />
              <p className="text-xs text-emerald-100/50">Pflegevorgänge</p>
              <p className="text-sm font-semibold text-emerald-100 mt-0.5">{plant.care_history.length}</p>
            </div>
          )}
          {diary.length > 0 && (
            <div className="card-grow p-3 text-center">
              <BookOpen size={16} className="mx-auto text-emerald-400 mb-1" />
              <p className="text-xs text-emerald-100/50">Tagebuch</p>
              <p className="text-sm font-semibold text-emerald-100 mt-0.5">{diary.length} Einträge</p>
            </div>
          )}
          {harvest && (
            <div className="card-grow p-3 text-center">
              <Scale size={16} className="mx-auto text-yellow-400 mb-1" />
              <p className="text-xs text-emerald-100/50">Ernte</p>
              <p className="text-sm font-semibold text-yellow-400 mt-0.5">
                {harvest.dry_weight_g ? `${harvest.dry_weight_g}g` : 'Erfasst'}
              </p>
            </div>
          )}
        </div>

        {/* Strain Info */}
        {strain && (
          <div className="card-grow p-4">
            <h2 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2 text-sm">
              <FlaskConical size={14} className="text-emerald-400" /> Strain-Info
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <InfoItem label="Typ" value={strain.type} />
              {strain.thc_percent && <InfoItem label="THC" value={`${strain.thc_percent}%`} />}
              {strain.cbd_percent && <InfoItem label="CBD" value={`${strain.cbd_percent}%`} />}
              {strain.flowering_weeks && <InfoItem label="Blüte" value={`${strain.flowering_weeks} Wochen`} />}
            </div>
            {strain.description && (
              <p className="text-xs text-emerald-100/50 mt-2 leading-relaxed">{strain.description}</p>
            )}
          </div>
        )}

        {/* Notes */}
        {plant.notes && (
          <div className="card-grow p-4">
            <h2 className="font-heading font-semibold text-emerald-100 mb-2 flex items-center gap-2 text-sm">
              <BookOpen size={14} className="text-emerald-400" /> Notizen
            </h2>
            <p className="text-sm text-emerald-100/70 leading-relaxed">{plant.notes}</p>
          </div>
        )}

        {/* Harvest Data */}
        {harvest && (
          <div className="card-grow p-4" data-testid="plant-harvest-section">
            <h2 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2 text-sm">
              <Scale size={14} className="text-yellow-400" /> Ernte-Daten
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {harvest.harvest_date && (
                <InfoItem label="Erntedatum" value={new Date(harvest.harvest_date).toLocaleDateString('de-DE')} />
              )}
              {harvest.wet_weight_g  && <InfoItem label="Nassgewicht"   value={`${harvest.wet_weight_g}g`} />}
              {harvest.dry_weight_g  && <InfoItem label="Trockengewicht" value={`${harvest.dry_weight_g}g`} accent="yellow" />}
              {harvest.wet_weight_g  && harvest.dry_weight_g && (
                <InfoItem label="Ausbeute"
                  value={`${((harvest.dry_weight_g / harvest.wet_weight_g) * 100).toFixed(0)}%`}
                  accent="yellow" />
              )}
            </div>
            {harvest.quality > 0 && (
              <div className="mt-3 flex items-center gap-1">
                <span className="text-xs text-emerald-100/50 mr-1">Qualität:</span>
                {[1,2,3,4,5].map(n => (
                  <Star key={n} size={14}
                    className={n <= harvest.quality ? 'text-yellow-400' : 'text-emerald-100/20'}
                    fill={n <= harvest.quality ? 'currentColor' : 'none'} />
                ))}
              </div>
            )}
            {harvest.notes && <p className="text-xs text-emerald-100/50 mt-2">{harvest.notes}</p>}
          </div>
        )}

        {/* Care History */}
        {plant.care_history?.length > 0 && (
          <div className="card-grow p-4" data-testid="care-history-section">
            <h2 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2 text-sm">
              <CalendarCheck size={14} className="text-emerald-400" />
              Pflegehistorie
              <span className="text-xs text-emerald-100/30 font-normal ml-1">({plant.care_history.length})</span>
            </h2>
            <div className="space-y-2">
              {careToShow.map(log => {
                const col  = careColor(log.care_type);
                const Icon = CARE_ICONS[log.care_type] || CalendarCheck;
                return (
                  <div key={log.id} className="flex items-start gap-3 p-2.5 rounded-lg"
                    style={{ background: col.bg, border: `1px solid ${col.text}30` }}
                    data-testid={`care-log-${log.id}`}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${col.text}20` }}>
                      <Icon size={13} style={{ color: col.text }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium" style={{ color: col.text }}>
                          {CARE_LABELS[log.care_type] || log.care_type}
                        </span>
                        <span className="text-xs text-emerald-100/30 flex-shrink-0">
                          {log.performed_at
                            ? new Date(log.performed_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
                            : ''}
                        </span>
                      </div>
                      {log.amount && <span className="text-xs text-emerald-100/50">{log.amount}</span>}
                      {log.notes  && <p className="text-xs text-emerald-100/40 truncate mt-0.5">{log.notes}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
            {plant.care_history.length > 5 && (
              <button onClick={() => setShowAllCare(v => !v)}
                className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                style={{ border: '1px solid rgba(16,185,129,0.2)' }}
                data-testid="show-all-care-btn">
                {showAllCare
                  ? <><ChevronUp size={13} /> Weniger anzeigen</>
                  : <><ChevronDown size={13} /> Alle {plant.care_history.length} anzeigen</>}
              </button>
            )}
          </div>
        )}

        {/* Diary Entries */}
        {diary.length > 0 && (
          <div className="card-grow p-4" data-testid="diary-section">
            <h2 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2 text-sm">
              <BookOpen size={14} className="text-indigo-400" />
              Tagebuch
              <span className="text-xs text-emerald-100/30 font-normal ml-1">({diary.length})</span>
            </h2>
            <div className="space-y-2">
              {diary.map(entry => {
                const isExpanded = expandedDiary === entry.id;
                return (
                  <div key={entry.id} className="rounded-lg overflow-hidden"
                    style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}
                    data-testid={`diary-entry-${entry.id}`}>
                    <button className="w-full flex items-center justify-between p-3 text-left"
                      onClick={() => setExpandedDiary(isExpanded ? null : entry.id)}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-indigo-300 truncate">{entry.title}</p>
                        <p className="text-xs text-emerald-100/30 mt-0.5">
                          {entry.created_at ? new Date(entry.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
                        </p>
                      </div>
                      {isExpanded
                        ? <ChevronUp size={14} className="text-indigo-400/50 flex-shrink-0 ml-2" />
                        : <ChevronDown size={14} className="text-indigo-400/50 flex-shrink-0 ml-2" />}
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3">
                        <p className="text-sm text-emerald-100/70 leading-relaxed whitespace-pre-wrap">{entry.content}</p>
                        {entry.images?.length > 0 && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            {entry.images.map((img, i) => (
                              <img key={i} src={resolveImageUrl(img)} alt="" className="h-20 w-20 object-cover rounded-lg" />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!plant.care_history?.length && !diary.length && (
          <div className="card-grow p-8 text-center">
            <Leaf size={36} className="mx-auto text-emerald-500/20 mb-3" />
            <p className="text-sm text-emerald-100/30">Noch keine Pflegevorgänge oder Tagebucheinträge</p>
            <div className="flex gap-3 justify-center mt-4">
              <Link to="/care-log" className="btn-secondary text-sm flex items-center gap-1.5">
                <CalendarCheck size={13} /> Pflege eintragen
              </Link>
              <Link to="/diary" className="btn-secondary text-sm flex items-center gap-1.5">
                <BookOpen size={13} /> Tagebuch
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* ── Edit Modal ───────────────────────────────────────────────────── */}
      {showEdit && (
        <div className="modal-backdrop" data-testid="plant-edit-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">Pflanze bearbeiten</h2>
              <button onClick={() => setShowEdit(false)} className="text-emerald-100/40 hover:text-emerald-100">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-3">
              {/* Name */}
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Name *</label>
                <input
                  className="input-grow"
                  value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  data-testid="edit-plant-name-input"
                />
              </div>

              {/* Phase */}
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Phase</label>
                <div className="grid grid-cols-3 gap-2">
                  {PHASE_OPTIONS.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setEditForm(f => ({ ...f, phase: opt.value }))}
                      className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
                        editForm.phase === opt.value
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          : 'text-emerald-100/50 hover:bg-[#050A07]'
                      }`}
                      style={{ border: editForm.phase === opt.value ? undefined : '1px solid #1B2A21' }}
                      data-testid={`edit-phase-${opt.value}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Strain + Gruppe */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Strain</label>
                  <select className="input-grow" value={editForm.strain_id}
                    onChange={e => setEditForm(f => ({ ...f, strain_id: e.target.value }))}
                    data-testid="edit-strain-select">
                    <option value="">Keiner</option>
                    {strains.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Gruppe</label>
                  <select className="input-grow" value={editForm.grow_group_id}
                    onChange={e => setEditForm(f => ({ ...f, grow_group_id: e.target.value }))}
                    data-testid="edit-group-select">
                    <option value="">Keine</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Startdatum */}
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Startdatum</label>
                <DatePicker
                  value={editForm.start_date}
                  onChange={v => setEditForm(f => ({ ...f, start_date: v }))}
                  placeholder="Startdatum wählen"
                  data-testid="edit-start-date-input"
                />
              </div>

              {/* Foto */}
              <div className="text-xs text-emerald-100/40 italic py-1">ð¡ Bild wird automatisch vom Strain Ã¼bernommen</div>

              {/* Notizen */}
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Notizen</label>
                <textarea className="input-grow" rows={3}
                  value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="edit-notes-input" />
              </div>

              {editError && <p className="text-red-400 text-sm">{editError}</p>}

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowEdit(false)} className="btn-secondary flex-1">
                  Abbrechen
                </button>
                <button type="submit" disabled={saving}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="save-edit-plant-btn">
                  <Save size={14} />
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value, accent }) {
  return (
    <div>
      <p className="text-xs text-emerald-100/40">{label}</p>
      <p className={`text-sm font-semibold mt-0.5 ${accent === 'yellow' ? 'text-yellow-400' : 'text-emerald-100'}`}>
        {value || '–'}
      </p>
    </div>
  );
}
